import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  ThemeProvider,
  createTheme,
  CssBaseline,
} from '@mui/material';
import {
  CloudUpload,
  Description,
  ArrowForward,
} from '@mui/icons-material';
import gesLogo from '../FileUpload/ges-logos.png';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1B3B58',
      light: '#81B3C0',
      dark: '#1B3B58',
    },
    secondary: {
      main: '#DD7C25',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    h2: {
      fontWeight: 800,
      letterSpacing: '-1px',
    },
    h3: {
      fontWeight: 700,
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 600,
          fontSize: '1rem',
          padding: '12px 32px',
        },
      },
    },
  },
});

const Landing = () => {
  const navigate = useNavigate();

  const handleFileUploadClick = () => {
    navigate('/file-upload');
  };

  const handleDataUploadClick = () => {
    navigate('/upload');
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #3C5A77 0%, #A7CFD8 25%, #C5BE86 50%, #F2A85C 75%, #D98C6E 100%)',
        overflow: 'hidden',
        position: 'relative',
      }}>

        {/* Top-left GES logo */}
        <Box
          component="img"
          src={gesLogo}
          alt="GES Logo"
          sx={{
            position: 'absolute',
            top: 24,
            left: 24,
            height: 64,
            width: 'auto',
            objectFit: 'contain',
            zIndex: 10,
            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))',
          }}
        />

        {/* Decorative gradient orbs */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            right: '-5%',
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(221, 124, 37, 0.22) 0%, rgba(221, 124, 37, 0) 70%)',
            filter: 'blur(80px)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-10%',
            left: '-5%',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(27, 59, 88, 0.22) 0%, rgba(27, 59, 88, 0) 70%)',
            filter: 'blur(100px)',
            pointerEvents: 'none',
          }}
        />

        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 1,
            px: { xs: 2, sm: 3 },
          }}
        >

          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              mx: 'auto',
              maxWidth: 1100,
            }}
          >


            {/* Header Section */}
            <Box
              sx={{
                textAlign: 'center',
                mb: 8,
                maxWidth: 950,   
                mx: 'auto',      
              }}
            >

              <Typography
                variant="h2"
                sx={{
                  mb: 3,
                  color: '#1B3B58',
                  fontSize: { xs: '1.6rem', sm: '2.4rem', md: '3.2rem' },
                  background: 'linear-gradient(135deg, #1B3B58 0%, #DD7C25 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-1px',
                  fontWeight: 800,
                  animation: 'fadeInDown 0.8s ease-out',
                  '@keyframes fadeInDown': {
                    from: {
                      opacity: 0,
                      transform: 'translateY(-30px)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
                GES Upload Assistant
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  mb: 4,
                  color: '#64748b',
                  fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.1rem' },
                  fontWeight: 500,
                  maxWidth: 650,
                  mx: 'auto',
                  lineHeight: 1.6,
                  animation: 'fadeInUp 0.8s ease-out 0.2s both',
                  '@keyframes fadeInUp': {
                    from: {
                      opacity: 0,
                      transform: 'translateY(30px)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
                Choose your upload type and let our intelligent system handle your data seamlessly
              </Typography>

              {/* Divider */}
              <Box
                sx={{
                  width: 80,
                  height: 4,
                  background: 'linear-gradient(90deg, transparent, #DD7C25, transparent)',
                  borderRadius: 2,
                  mx: 'auto',
                  animation: 'scaleX 0.8s ease-out 0.4s both',
                  '@keyframes scaleX': {
                    from: {
                      scaleX: 0,
                    },
                    to: {
                      scaleX: 1,
                    },
                  },
                }}
              />
            </Box>

            {/* Upload Options Cards */}
            <Grid
              container
              spacing={{ xs: 3, sm: 4, md: 5 }}
              justifyContent="center"
              alignItems="stretch"
              sx={{
                maxWidth: 950,
                mx: 'auto',
                mb: 8,
              }}
            >


              {/* File Upload Card */}
              <Grid item xs={12} sm={6}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(255, 244, 235, 0.96) 0%, rgba(255, 250, 245, 0.96) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '2px solid rgba(221, 124, 37, 0.2)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    animation: 'slideInLeft 0.8s ease-out 0.3s both',
                    '@keyframes slideInLeft': {
                      from: {
                        opacity: 0,
                        transform: 'translateX(-30px)',
                      },
                      to: {
                        opacity: 1,
                        transform: 'translateX(0)',
                      },
                    },
                    '&:hover': {
                      transform: 'translateY(-12px)',
                      boxShadow: '0 30px 80px rgba(221, 124, 37, 0.2)',
                      borderColor: '#DD7C25',
                    },
                  }}
                  onClick={handleFileUploadClick}
                >
                  <CardContent sx={{ textAlign: 'center', p: { xs: 3, sm: 4 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Icon Container */}
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        mx: 'auto',
                        mb: 3,
                        background: 'linear-gradient(135deg, #DD7C25 0%, #B45B2C 100%)',
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 12px 30px rgba(221, 124, 37, 0.3)',
                        transition: 'all 0.3s ease',
                        animation: 'float 3s ease-in-out infinite',
                        '@keyframes float': {
                          '0%, 100%': { transform: 'translateY(0px)' },
                          '50%': { transform: 'translateY(-15px)' },
                        },
                      }}
                    >
                      <CloudUpload sx={{ fontSize: 56, color: '#ffffff' }} />
                    </Box>

                    <Typography
                      variant="h5"
                      sx={{
                        mb: 2,
                        color: '#1B3B58',
                        fontWeight: 700,
                        fontSize: { xs: '1.3rem', sm: '1.4rem' },
                      }}
                    >
                      File Upload
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        mb: 3,
                        color: '#64748b',
                        fontSize: { xs: '0.9rem', sm: '0.95rem' },
                        lineHeight: 1.6,
                        flex: 1,
                      }}
                    >
                      Upload your Excel files and let our intelligent preprocessor agent analyze and optimize your data automatically
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        color: '#DD7C25',
                        fontWeight: 600,
                        fontSize: { xs: '0.9rem', sm: '0.95rem' },
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          gap: 1.5,
                        },
                      }}
                    >
                      Get Started
                      <ArrowForward sx={{ fontSize: 20 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Data Upload Card */}
              <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(240, 246, 250, 0.96) 0%, rgba(248, 251, 253, 0.96) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '2px solid rgba(27, 59, 88, 0.2)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    animation: 'slideInRight 0.8s ease-out 0.3s both',
                    '@keyframes slideInRight': {
                      from: {
                        opacity: 0,
                        transform: 'translateX(30px)',
                      },
                      to: {
                        opacity: 1,
                        transform: 'translateX(0)',
                      },
                    },
                    '&:hover': {
                      transform: 'translateY(-12px)',
                      boxShadow: '0 30px 80px rgba(27, 59, 88, 0.2)',
                      borderColor: '#1B3B58',
                    },
                  }}
                  onClick={handleDataUploadClick}
                >
                  <CardContent sx={{ textAlign: 'center', p: { xs: 3, sm: 4 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Icon Container */}
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        mx: 'auto',
                        mb: 3,
                        background: 'linear-gradient(135deg, #1B3B58 0%, #2d5a7b 100%)',
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 12px 30px rgba(27, 59, 88, 0.3)',
                        transition: 'all 0.3s ease',
                        animation: 'float 3s ease-in-out infinite 0.5s',
                        '@keyframes float': {
                          '0%, 100%': { transform: 'translateY(0px)' },
                          '50%': { transform: 'translateY(-15px)' },
                        },
                      }}
                    >
                      <Description sx={{ fontSize: 56, color: '#ffffff' }} />
                    </Box>

                    <Typography
                      variant="h5"
                      sx={{
                        mb: 2,
                        color: '#1B3B58',
                        fontWeight: 700,
                        fontSize: { xs: '1.3rem', sm: '1.4rem' },
                      }}
                    >
                      Salesforce Data Upload
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        mb: 3,
                        color: '#64748b',
                        fontSize: { xs: '0.9rem', sm: '0.95rem' },
                        lineHeight: 1.6,
                        flex: 1,
                      }}
                    >
                      Upload your cleaned data with Salesforce Opportunity ID for seamless data upload automation and integration
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        color: '#1B3B58',
                        fontWeight: 600,
                        fontSize: { xs: '0.9rem', sm: '0.95rem' },
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          gap: 1.5,
                        },
                      }}
                    >
                      Get Started
                      <ArrowForward sx={{ fontSize: 20 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

            </Grid>

            {/* Bottom Info Section */}
            {/* <Paper
              elevation={0}
              sx={{
                maxWidth: 750,
                mx: 'auto',
                p: { xs: 3, sm: 4 },
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(248, 250, 252, 0.8) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(221, 124, 37, 0.15)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                animation: 'fadeInUp 0.8s ease-out 0.6s both',
                '@keyframes fadeInUp': {
                  from: {
                    opacity: 0,
                    transform: 'translateY(30px)',
                  },
                  to: {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
                width: '100%',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  textAlign: 'center',
                  color: '#64748b',
                  mb: 2,
                  fontSize: { xs: '0.9rem', sm: '0.95rem' },
                  lineHeight: 1.5,
                }}
              >
                <strong style={{ color: '#1B3B58' }}>Powered by AI</strong> — Our intelligent agents analyze, validate, optimize & assist in your data uploads for best results
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  textAlign: 'center',
                  display: 'block',
                  color: '#94a3b8',
                  fontSize: '0.8rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                }}
              >
                Secure • Fast • Reliable
              </Typography>
            </Paper> */}

          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Landing;
